import { SeeMoreCard, WhiteBox } from "@foodpilot/foods";
import { Grid2, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CompanyOverview } from "src/api/client-api/company/overview/useOrganizationOverview";
import { HistogramOverview } from "./HistogramOverview";
import { StrategyHeader } from "./StrategyHeader";
import { isAppInPDFMode } from "src/utils/rendering";
import { ActionPlanOverviewList, getActionPlanOverviewList } from "./getActionPlanOverviewList";
import { LockedStrategy } from "../../OneScoreOverview/Strategy/LockedStrategy";
import { EmptyStrategy } from "../../OneScoreOverview/Strategy/EmptyStrategy";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { getSsqDisplayTitle } from "src/utils/ssqTitle";

type StrategyOverviewProps = {
    overview: CompanyOverview;
};

export const StrategyOverview = (props: StrategyOverviewProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isPdfMode = isAppInPDFMode();

    const { overview } = props;
    const actionPlanList = getActionPlanOverviewList({ overview });

    const previewItems = actionPlanList.slice(0, 2);
    const seeMoreItems = actionPlanList.slice(2, actionPlanList.length);

    const isSingleItem = actionPlanList.length === 1;
    const columnSize = isSingleItem ? 12 : 6;

    if (isPdfMode) {
        return (
            <WhiteBox>
                <Stack gap={theme.spacing(2)}>
                    <StrategyHeader actionPlansCount={actionPlanList.length} />
                    <Grid2 container spacing={theme.spacing(1)}>
                        {actionPlanList.map((item, index) => {
                            if (item.existingActionPlan === undefined) return null;

                            return (
                                // Maybe instead of linkedScore being possibly undefined, here we'd want to filter
                                // with null above in case it actually is undefined.
                                <Grid2 size={{ xs: 12, md: 6 }} key={`pdf-all-${item.linkedScore?.id}-${index}`}>
                                    <HistogramOverview
                                        ssqId={item.ssq.id}
                                        scoreId={item.existingActionPlan.score.id}
                                        title={item.linkedScore?.title ?? ""}
                                        currentValue={item.linkedScore?.value ?? null}
                                        start={item.existingActionPlan.start}
                                        end={item.existingActionPlan.end}
                                        unit={item.existingActionPlan.unit}
                                    />
                                </Grid2>
                            );
                        })}
                    </Grid2>
                </Stack>
            </WhiteBox>
        );
    }

    return (
        <WhiteBox>
            <SeeMoreCard
                seeMoreText={t("components.see_more.action_plan", "See more action plans")}
                seeLessText={t("components.see_less.action_plan", "See less action plans")}
                hidden={
                    seeMoreItems.length > 0 ?
                        <Grid2 container spacing={theme.spacing(1)}>
                            {seeMoreItems.map((item, index) => {
                                return (
                                    <Grid2
                                        key={`see-more-${item.linkedScore?.id}-${item.ssq.id}-${index}`}
                                        size={{ xs: 12, md: columnSize }}
                                    >
                                        <StrategyOverviewItem item={item} />
                                    </Grid2>
                                );
                            })}
                        </Grid2>
                    :   undefined
                }
                defaultVisible={false}
            >
                <Stack gap={theme.spacing(2)}>
                    <StrategyHeader actionPlansCount={actionPlanList.length} />
                    <Grid2 container spacing={theme.spacing(1)}>
                        {previewItems.map((item, index) => {
                            return (
                                <Grid2
                                    key={`see-more-shown-${item.linkedScore?.id}-${item.ssq.id}-${index}`}
                                    size={{ xs: 12, md: columnSize }}
                                >
                                    <StrategyOverviewItem item={item} />
                                </Grid2>
                            );
                        })}
                    </Grid2>
                </Stack>
            </SeeMoreCard>
        </WhiteBox>
    );
};

type StrategyOverviewItemProps = {
    item: ActionPlanOverviewList;
};
const StrategyOverviewItem = (props: StrategyOverviewItemProps) => {
    const { item } = props;
    const { company } = useCompanyMetadata();

    if (item.isActionable === false) {
        return null;
    }

    if (item.existingActionPlan) {
        return (
            <HistogramOverview
                ssqId={item.ssq.id}
                scoreId={item.existingActionPlan.score.id}
                title={item.linkedScore?.title ?? ""}
                currentValue={item.linkedScore?.value ?? null}
                start={item.existingActionPlan.start}
                end={item.existingActionPlan.end}
                unit={item.existingActionPlan.unit}
            />
        );
    }

    if (item.ssq.post?.isLocked === false) {
        return <LockedStrategy questionnaireName={getSsqDisplayTitle(item.ssq)} />;
    }

    return (
        <EmptyStrategy companyId={company.id} ssqId={item.ssq.id} questionnaireName={getSsqDisplayTitle(item.ssq)} />
    );
};
